import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import type { Referral } from '@/hooks/useReferrals';
import { Button } from '@/components/ui/Button';
import { ApiCallError, callFunction } from '@/lib/api';
import { formatDanishDateTime } from '@/lib/danishDateTime';
import { downloadMedcomDocument } from '@/lib/medcom/encodeMedcomDocument';
import {
  findReferralExchange,
  referralReceipts,
  type CtlExchange,
  type ReceiptMatchCheck,
  type ReferralReceipt,
} from '@/lib/referral/referralReceiptMatch';

const SECTION = 'overflow-hidden rounded-lg border border-ds-line bg-ds-card shadow-card';
const HEADING = 'text-xs font-medium uppercase tracking-wide text-ds-muted';
const RECEIPT_LIST_LIMIT = 100;

type ListOutput = { exchanges: CtlExchange[] };

async function listExchanges(clinicId: string, query: { search?: string; kind?: string }): Promise<CtlExchange[]> {
  const data = await callFunction<Record<string, unknown>, ListOutput>('communicate-ctl', {
    action: 'list',
    clinicId,
    limit: RECEIPT_LIST_LIMIT,
    ...query,
  });
  return data.exchanges;
}

/**
 * DIS01-S 4.2: the positive receipt sent for this received referral, checked
 * against the referral's envelope, letter and EANs, so a screenshot documents it.
 */
export function ReferralReceiptStatus({ referral }: { referral: Referral }) {
  const { t } = useTranslation();
  const [busyId, setBusyId] = useState<string | null>(null);
  const letter = referral.medcom_letter_identifier;
  const query = useQuery({
    queryKey: ['referral-receipt', referral.clinic_id, referral.id, referral.medcom_exchange_id, letter],
    enabled: Boolean(referral.medcom_exchange_id || letter),
    staleTime: 30_000,
    queryFn: async (): Promise<{ exchange: CtlExchange | null; receipts: ReferralReceipt[] }> => {
      const [own, receipts] = await Promise.all([
        letter ? listExchanges(referral.clinic_id, { search: letter }) : Promise.resolve([]),
        listExchanges(referral.clinic_id, { kind: 'XCTL03,XCTL02' }),
      ]);
      const exchange = findReferralExchange(own, referral.medcom_exchange_id, letter);
      return { exchange, receipts: exchange ? referralReceipts(exchange, receipts) : [] };
    },
  });

  const checkLabel = (c: ReceiptMatchCheck) =>
    ({
      kind: t('referrals.receipt_check_kind', 'Positiv kvittering (XCTL03)'),
      envelope: t('referrals.receipt_check_envelope', 'OriginalEnvelopeIdentifier = henvisningens kuvert-id'),
      letter: t('referrals.receipt_check_letter', 'OriginalLetter = henvisningens brev-id'),
      receiver: t('referrals.receipt_check_receiver', 'Modtager-EAN = henvisningens afsender'),
      sender: t('referrals.receipt_check_sender', 'Afsender-EAN = henvisningens modtager'),
    })[c.id];

  async function download(receipt: CtlExchange) {
    setBusyId(receipt.id);
    try {
      const result = await callFunction<Record<string, unknown>, { xml: string }>('communicate-ctl', {
        action: 'payload',
        clinicId: referral.clinic_id,
        id: receipt.id,
      });
      downloadMedcomDocument(`${receipt.kind}_${receipt.envelopeIdentifier ?? receipt.id}.xml`, result.xml);
    } catch (err) {
      toast.error(err instanceof ApiCallError ? err.message : t('referrals.receipt_download_failed', 'Kvitteringen kunne ikke hentes.'));
    } finally {
      setBusyId(null);
    }
  }

  const data = query.data;
  return (
    <section className={SECTION} data-testid="referral-receipt-status">
      <div className="border-b border-ds-line px-4 py-2.5">
        <span className={HEADING}>{t('referrals.receipt_title', 'Kvittering for henvisningen')}</span>
      </div>
      <div className="space-y-3 px-4 py-3 text-sm">
        {query.isLoading && <p className="text-ds-muted">{t('referrals.receipt_loading', 'Henter kvitteringer …')}</p>}
        {query.error && (
          <p role="alert" className="text-ds-red-ink">
            {query.error instanceof ApiCallError ? query.error.message : t('referrals.receipt_load_failed', 'Kvitteringerne kunne ikke hentes.')}
          </p>
        )}
        {data && !data.exchange && (
          <p className="text-ds-muted">{t('referrals.receipt_no_exchange', 'Henvisningens udveksling blev ikke fundet i udvekslingsloggen.')}</p>
        )}
        {data?.exchange && (
          <p className="text-ds-muted" data-testid="referral-receipt-referral">
            {t('referrals.receipt_referral', 'Henvisning: kuvert {{envelope}}, brev {{letter}}, fra EAN {{sender}} til EAN {{receiver}}.', {
              envelope: data.exchange.envelopeIdentifier ?? '—',
              letter: data.exchange.letterIdentifier ?? '—',
              sender: data.exchange.senderEan ?? '—',
              receiver: data.exchange.receiverEan ?? '—',
            })}
          </p>
        )}
        {data?.exchange && data.receipts.length === 0 && (
          <p className="text-ds-amber-ink" data-testid="referral-receipt-none">
            {t('referrals.receipt_none', 'Der er ikke fundet en kvittering, der svarer på denne henvisnings kuvert.')}
          </p>
        )}
        {data?.receipts.map(({ receipt, checks, matches }) => (
          <div
            key={receipt.id}
            className={`rounded-md border px-3 py-2 ${matches ? 'border-ds-line' : 'border-ds-amber-line bg-ds-amber-tint'}`}
            data-testid="referral-receipt"
          >
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="font-medium text-ds-text">
                {receipt.kind} · {t('referrals.receipt_envelope', 'kuvert')} {receipt.envelopeIdentifier ?? '—'} · {formatDanishDateTime(receipt.createdAt)}
              </span>
              <span className="flex items-center gap-2">
                <span
                  className={`rounded px-1.5 py-0.5 text-xs font-medium ${matches ? 'bg-ds-teal-tint text-ds-teal' : 'bg-ds-amber-tint text-ds-amber-ink'}`}
                  data-testid="referral-receipt-verdict"
                >
                  {matches
                    ? t('referrals.receipt_matches', 'Passer til henvisningen')
                    : t('referrals.receipt_mismatch', 'Passer ikke til henvisningen')}
                </span>
                <Button type="button" size="sm" variant="secondary" disabled={busyId === receipt.id} onClick={() => void download(receipt)}>
                  {t('referrals.receipt_download', 'Hent XML')}
                </Button>
              </span>
            </div>
            <ul className="mt-2 space-y-0.5 text-xs">
              {checks.map((c) => (
                <li key={c.id} className={c.pass ? 'text-ds-muted' : 'text-ds-amber-ink'}>
                  {c.pass ? '✓' : '✗'} {checkLabel(c)}: {c.actual || '—'}
                  {!c.pass && ` (${t('referrals.receipt_expected', 'forventet')} ${c.expected || '—'})`}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </section>
  );
}
